import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { lambdaService, FileInfo } from '../../services/lambdaService';

const formatSize = (bytes: number) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

interface FlowFileSelectorProps {
  selectedFile: FileInfo | null;
  onFileSelect: (file: FileInfo | null) => void;
}

export const FlowFileSelector: React.FC<FlowFileSelectorProps> = ({ selectedFile, onFileSelect }) => {
  const [files, setFiles] = useState<FileInfo[]>([]);
  const [loading, setLoading] = useState(false);

  const loadFiles = useCallback(async () => {
    setLoading(true);
    try {
      const res = await lambdaService.listFiles();
      if (res.success) setFiles(res.files);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  const handleSelect = (fileName: string) => {
    const f = files.find((fi) => fi.fileName === fileName);
    if (f) onFileSelect(f);
  };

  return (
    <div className="flex items-center gap-1 nodrag">
      <Select value={selectedFile?.fileName || ''} onValueChange={handleSelect}>
        <SelectTrigger className="h-7 text-[11px] flex-1">
          <SelectValue placeholder={loading ? 'Loading...' : 'Select file...'} />
        </SelectTrigger>
        <SelectContent>
          {files.map((f) => (
            <SelectItem key={f.fileName} value={f.fileName} className="text-xs">
              <div className="flex items-center gap-1.5">
                <span className="truncate max-w-[180px]">{f.fileName}</span>
                <span className="text-muted-foreground text-[10px]">{formatSize(f.fileSize)}</span>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <button
        onClick={loadFiles}
        disabled={loading}
        className="h-7 w-7 shrink-0 flex items-center justify-center rounded-md hover:bg-muted transition-colors"
      >
        {loading ? (
          <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
        ) : (
          <RefreshCw className="h-3 w-3 text-muted-foreground" />
        )}
      </button>
    </div>
  );
};
